import { FC, ReactNode, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SearchContextProvider, useSearchContext } from "../../../../../context/searchContext";
import useI18n from "../../../../../hooks/useTranslations";
import { CustomError } from "../../../../../http/errors";
import { SystemTypeId } from "../../../../../http/vertigrip/types/systemTypes";
import { useSystemTypesApi } from "../../../../../http/vertigrip/useSystemTypes";
import FlexContent from "../../../../ui/FlexContent";
import Header, { HeaderPath } from "../../../../ui/Header";
import Tabs, { Tab } from "../../../../ui/Tabs";
import { VERTIGRIP } from "../../AdminSystemSettings";
import SystemTypeIntermediateElements from "../intermediateElements/SystemTypeIntermediateElements";
import Shuttles from "./shuttles/Shuttles";
import SystemTypeDetails from "./SystemTypeDetails";

const SystemType: FC = () => {
  const i18n = useI18n();
  const { systemId } = useParams<{ systemId: string }>();
  const { systemTypeId } = useParams<{ systemTypeId: SystemTypeId }>();
  const systemTypesApi = useSystemTypesApi();
  const [searchParams] = useSearchParams();
  const selectedIndex = Number(searchParams.get("tab")) || 0;

  const aborter = useRef(new AbortController());
  const { error, data, trigger } = systemTypesApi.getAdminSystemType(aborter.current.signal);

  const tabs: Tab[] = [
    { title: i18n.translation.common.generalData, hasSubTabs: true },
    { title: i18n.translation.intermediateElements.plural },
    { title: i18n.translation.shuttles.plural },
  ];

  useEffect(() => {
    systemTypeId && trigger(systemTypeId);
  }, [systemTypeId]);

  const path = [
    { url: `/admin/systems`, display: i18n.translation.systems.all },
    { url: `/admin/systems/${systemId}`, display: VERTIGRIP },
    {
      url: `/admin/systems/${systemId}/settings?tab=4`,
      display: `${i18n.translation.common.settings}: ${i18n.translation.systemTypes.plural}`,
    },
    { display: data?.name ?? "" },
  ];

  const contents: ReactNode[] = [
    <FlexContent padding direction="row">
      <SystemTypeDetails systemType={!error ? data : undefined} disabled={!!error || !data} />
    </FlexContent>,
    <SystemTypeIntermediateElements active={selectedIndex === 1} />,
    <Shuttles active={selectedIndex === 2} />,
  ];

  return (
    <SearchContextProvider>
      <SystemTypeContent title={data?.name ?? ""} error={error} path={path} tabs={tabs} contents={contents} />
    </SearchContextProvider>
  );
};

type SystemTypeContentProps = {
  title: string;
  error?: CustomError;
  path: HeaderPath[];
  tabs: Tab[];
  contents: ReactNode[];
};

const SystemTypeContent: FC<SystemTypeContentProps> = ({ title, error, path, tabs, contents }) => {
  const { actions, title: tabTitle } = useSearchContext();

  return (
    <>
      <Header title={tabTitle ? `${title} · ${tabTitle}` : title} error={error} path={path} buttons={actions} />
      <Tabs urlParameter="tab" tabs={tabs} contents={contents} />
    </>
  );
};

export default SystemType;
